/**
 * Keyboard + pointer-lock mouse input, plus a synthetic layer for touch
 * (spec 0029). One instance owns the canvas. TouchControls injects synthetic
 * key state / axes / look deltas so every existing read (`pressed`, `axis`,
 * `consumeMouseDelta`) drives the game unchanged; with no touch session the
 * synthetic layer is empty and behavior is identical to keyboard-only.
 */
export class Input {
  private readonly keys = new Set<string>();
  private readonly syntheticKeys = new Set<string>();
  private readonly syntheticAxes = new Map<string, number>();
  private touchSession = false;
  private mouseDx = 0;
  private mouseDy = 0;
  private locked = false;

  constructor(private readonly lockTarget: HTMLElement) {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });
    window.addEventListener('blur', () => {
      this.keys.clear();
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.lockTarget;
      if (!this.locked) {
        this.keys.clear();
      }
    });
    window.addEventListener('mousemove', (e) => {
      if (this.locked) {
        this.mouseDx += e.movementX;
        this.mouseDy += e.movementY;
      }
    });
  }

  requestLock(): void {
    this.lockTarget.requestPointerLock();
  }

  get isLocked(): boolean {
    return this.locked;
  }

  /** True while any play session is live: pointer lock (desktop) OR touch. */
  get isActive(): boolean {
    return this.locked || this.touchSession;
  }

  /** Press/release a synthetic key (touch buttons: Space, KeyR, ability keys). */
  setSynthetic(code: string, down: boolean): void {
    if (down) {
      this.syntheticKeys.add(code);
    } else {
      this.syntheticKeys.delete(code);
    }
  }

  /** Set a synthetic analog axis value in [-1,1] (the touch move stick). */
  setSyntheticAxis(posCode: string, negCode: string, value: number): void {
    this.syntheticAxes.set(`${posCode}|${negCode}`, Math.max(-1, Math.min(1, value)));
  }

  /** Add look motion (touch drag) into the same accumulators mouse-look reads. */
  injectMouseDelta(dx: number, dy: number): void {
    this.mouseDx += dx;
    this.mouseDy += dy;
  }

  /** Start/end a touch play session; ending clears all synthetic state. */
  setTouchSession(active: boolean): void {
    this.touchSession = active;
    if (!active) {
      this.syntheticKeys.clear();
      this.syntheticAxes.clear();
      this.mouseDx = 0;
      this.mouseDy = 0;
    }
  }

  pressed(code: string): boolean {
    return this.keys.has(code) || this.syntheticKeys.has(code);
  }

  /** Axis helper: keyboard (+1/-1) plus any synthetic axis, clamped to [-1,1]. */
  axis(posCode: string, negCode: string): number {
    const keyboard = (this.keys.has(posCode) ? 1 : 0) - (this.keys.has(negCode) ? 1 : 0);
    const synthetic = this.syntheticAxes.get(`${posCode}|${negCode}`) ?? 0;
    return Math.max(-1, Math.min(1, keyboard + synthetic));
  }

  /** Returns accumulated mouse motion since the last call and resets it. */
  consumeMouseDelta(): { dx: number; dy: number } {
    const delta = { dx: this.mouseDx, dy: this.mouseDy };
    this.mouseDx = 0;
    this.mouseDy = 0;
    return delta;
  }
}
